// ./src/hooks/useSignalR.ts
import { useState, useEffect, useCallback, useRef } from "react";
import { WebPubSubClient } from "@azure/web-pubsub-client";
import { gql } from "@apollo/client";
import { ApiService } from "@/utils/api";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";

const NEGOTIATE_MUTATION = gql`
  mutation Negotiate {
    negotiate {
      code
      errors {
        name
        errors
        warnings
      }
      data {
        url
        accessToken
        displayId
        accountId
      }
    }
  }
`;

const SEND_COMMAND_MUTATION = gql`
  mutation SendCommand($params: CommandDataInput) {
    sendCommand(params: $params) {
      code
      data {
        command
        payload
        displayId
        activationId
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

export interface SignalRMessage {
  type: string;
  timestamp: string;
  [key: string]: any;
}

export interface CommandData {
  command: string;
  payload?: string | null | undefined;
  displayId?: string[] | null | undefined;
  activationId?: string[] | null | undefined;
}

export interface CommandDataInput {
  command: string;
  payload?: string | null | undefined;
  displayId?: string[] | null | undefined;
  activationId?: string[] | null | undefined;
}

export interface UseSignalROptions {
  /** Auto-connect on mount */
  autoConnect?: boolean;

  /** Enable debug logging */
  debug?: boolean;

  /** Custom reconnection delay in milliseconds */
  reconnectDelay?: number;

  /** Min delay before fetching data after receiving update (milliseconds) */
  minUpdateDelay?: number;

  /** Max delay before fetching data after receiving update (milliseconds) */
  maxUpdateDelay?: number;
}

// Map Web PubSub states to SignalR-like states for compatibility
enum ConnectionState {
  Disconnected = "Disconnected",
  Connecting = "Connecting",
  Connected = "Connected",
  Reconnecting = "Reconnecting",
}

export interface UseSignalRReturn {
  // Connection state
  connectionState: ConnectionState;
  isConnected: boolean;
  isConnecting: boolean;
  isReconnecting: boolean;

  // Connection operations
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  reconnect: () => Promise<void>;
  sendCommand: (params: CommandDataInput) => Promise<void>;

  // Event subscription
  onDataUpdated: (callback: (message: SignalRMessage) => void) => () => void;

  // Error handling
  lastError: string | null;
  clearError: () => void;

  // Display info
  displayId: string | null;
  accountId: string | null;
  activationId: string | null;
}

export const useSignalR = (
  options: UseSignalROptions = {}
): UseSignalRReturn => {
  const {
    autoConnect = true,
    debug = true,
    reconnectDelay = 5000,
    minUpdateDelay = 0,
    maxUpdateDelay = 30000,
  } = options;

  // Connection state
  const [connectionState, setConnectionState] = useState<ConnectionState>(
    ConnectionState.Disconnected
  );
  const [lastError, setLastError] = useState<string | null>(null);
  const [displayId, setDisplayId] = useState<string | null>(null);
  const [activationId, setActivationId] = useState<string | null>(null);
  const [accountId, setAccountId] = useState<string | null>(null);

  // Refs - store everything that shouldn't trigger re-renders
  const clientRef = useRef<WebPubSubClient | null>(null);
  const eventCallbacksRef = useRef<Set<(message: SignalRMessage) => void>>(
    new Set()
  );
  const isConnectingRef = useRef(false);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const connectionUrlRef = useRef<string | null>(null);
  const displayIdRef = useRef<string | null>(null);
  const accountIdRef = useRef<string | null>(null);

  // Computed states
  const isConnected = connectionState === ConnectionState.Connected;
  const isConnecting = connectionState === ConnectionState.Connecting;
  const isReconnecting = connectionState === ConnectionState.Reconnecting;

  // Clear error
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Random delay helper - stable, no dependencies
  const getRandomDelay = useCallback(() => {
    return (
      Math.floor(Math.random() * (maxUpdateDelay - minUpdateDelay + 1)) +
      minUpdateDelay
    );
  }, [minUpdateDelay, maxUpdateDelay]);

  // Handle incoming group messages - stable function
  const handleGroupMessage = useCallback(
    (message: any) => {
      try {
        const data = message.message?.data;

        if (!data) {
          if (debug) {
            console.log("SignalR: Received message without data", message);
          }
          return;
        }

        const parsedData = typeof data === "string" ? JSON.parse(data) : data;

        if (debug) {
          console.log("SignalR: Received dataUpdated event", parsedData);
        }

        const delay = getRandomDelay();

        if (debug) {
          console.log(`SignalR: Will process update in ${delay}ms`);
        }

        setTimeout(() => {
          eventCallbacksRef.current.forEach((callback) => {
            try {
              callback(parsedData);
            } catch (error) {
              console.error("SignalR: Error in event callback", error);
            }
          });
        }, delay);
      } catch (error) {
        console.error("SignalR: Error parsing group message", error);
      }
    },
    [debug, getRandomDelay]
  );

  // Setup client event handlers - stable function
  const setupClientHandlers = useCallback(
    (client: WebPubSubClient) => {
      client.on("connected", (e) => {
        if (debug) {
          console.log("SignalR: Connected", e);
          console.log("SignalR: Client is member of groups via token roles");
        }

        setConnectionState(ConnectionState.Connected);
        setLastError(null);
        isConnectingRef.current = false;
      });

      client.on("disconnected", (e) => {
        if (debug) {
          console.log("SignalR: Disconnected", e);
        }
        setConnectionState(ConnectionState.Disconnected);
        isConnectingRef.current = false;

        // Auto-reconnect if enabled and not a manual disconnect
        if (autoConnect && e?.message?.message !== "Manual disconnect") {
          if (debug) {
            console.log(
              `SignalR: Will attempt reconnect in ${reconnectDelay}ms`
            );
          }
          setConnectionState(ConnectionState.Reconnecting);
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, reconnectDelay);
        }
      });

      client.on("server-message", (e) => {
        console.log(`Received message ${e.message.data}`);
      });

      client.on("stopped", () => {
        console.log(`Stopped`);
      });

      client.on("group-message", handleGroupMessage);
    },
    [debug, autoConnect, reconnectDelay, handleGroupMessage]
  );

  // Register event listener
  const onDataUpdated = useCallback(
    (callback: (message: SignalRMessage) => void) => {
      eventCallbacksRef.current.add(callback);
      return () => {
        eventCallbacksRef.current.delete(callback);
      };
    },
    []
  );

  // Connect to Web PubSub
  const connect = useCallback(async () => {
    if (isConnectingRef.current) {
      if (debug) {
        console.log("SignalR: Connection attempt already in progress");
      }
      return;
    }

    if (clientRef.current && connectionState === ConnectionState.Connected) {
      if (debug) {
        console.log("SignalR: Already connected");
      }
      return;
    }

    isConnectingRef.current = true;
    setLastError(null);
    setConnectionState(ConnectionState.Connecting);

    try {
      // Only negotiate if we don't have a URL yet, or if reconnecting
      if (!connectionUrlRef.current) {
        if (debug) {
          console.log("SignalR: Starting negotiation");
        }

        const result = await ApiService.executeMutation(NEGOTIATE_MUTATION);

        if (
          result.code !== OP_RESULT_CODES.OK ||
          !result.data ||
          result.data.length === 0
        ) {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to negotiate SignalR connection";
          throw new Error(errorMessage);
        }

        const negotiateData = result.data[0];

        if (!negotiateData.url || !negotiateData.accessToken) {
          throw new Error(
            "Invalid negotiation response: missing url or accessToken"
          );
        }

        // Store negotiation data in refs
        connectionUrlRef.current = negotiateData.url;
        displayIdRef.current = negotiateData.displayId;
        accountIdRef.current = negotiateData.accountId;

        setDisplayId(negotiateData.displayId);
        setAccountId(negotiateData.accountId);
        setActivationId(negotiateData.activationId);

        if (debug) {
          console.log("SignalR: Negotiation successful", {
            displayId: negotiateData.displayId,
            accountId: negotiateData.accountId,
            activationId: negotiateData.activationId,
          });
        }
      }

      // Create client only once
      if (!clientRef.current) {
        const client = new WebPubSubClient(connectionUrlRef.current!);
        setupClientHandlers(client);
        clientRef.current = client;
      }

      // Start connection
      await clientRef.current.start();

      if (debug) {
        console.log("SignalR: Client started successfully");
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to connect to SignalR";
      console.error("SignalR: Connection error", error);
      setLastError(errorMessage);
      setConnectionState(ConnectionState.Disconnected);
      isConnectingRef.current = false;

      if (autoConnect) {
        if (debug) {
          console.log(
            `SignalR: Will attempt reconnect in ${reconnectDelay}ms after error`
          );
        }
        setConnectionState(ConnectionState.Reconnecting);
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, reconnectDelay);
      }
    }
  }, [
    debug,
    autoConnect,
    reconnectDelay,
    connectionState,
    setupClientHandlers,
  ]);

  const sendCommand = useCallback(
    async (params: CommandDataInput) => {
      // if (!isConnectingRef.current) {
      //   if (debug) {
      //     console.log("SignalR: There is no current connection");
      //   }
      //   return;
      // }

      // if (clientRef.current && connectionState !== ConnectionState.Connected) {
      //   if (debug) {
      //     console.log("SignalR: There is no conenction");
      //   }
      //   return;
      // }

      setLastError(null);

      try {
        if (debug) {
          console.log("SignalR: Sending command", params);
        }

        const result = await ApiService.executeMutation(SEND_COMMAND_MUTATION, {
          params,
        });

        if (result.didFail()) {
          setLastError(result.getErrorSummary());
        }

        if (debug) {
          console.log("SignalR: Send command result", result);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to send command via SignalR";

        setLastError(errorMessage);
      }
    },
    [debug, connectionState]
  );

  // Disconnect from Web PubSub
  const disconnect = useCallback(async () => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (!clientRef.current) return;

    try {
      if (debug) {
        console.log("SignalR: Disconnecting...");
      }

      // Mark as manual disconnect
      await clientRef.current.stop();
      clientRef.current = null;
      connectionUrlRef.current = null; // Clear URL to force re-negotiation
      setConnectionState(ConnectionState.Disconnected);

      if (debug) {
        console.log("SignalR: Disconnected");
      }
    } catch (error) {
      console.error("SignalR: Error during disconnect", error);
    }
  }, [debug]);

  // Reconnect (disconnect + connect)
  const reconnect = useCallback(async () => {
    await disconnect();
    await connect();
  }, [disconnect, connect]);

  // Auto-connect on mount
  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (clientRef.current) {
        clientRef.current.stop();
      }
    };
  }, []); // Empty deps - only run on mount/unmount

  return {
    connectionState: connectionState as any,
    isConnected,
    isConnecting,
    isReconnecting,
    connect,
    disconnect,
    reconnect,
    sendCommand,
    onDataUpdated,
    lastError,
    clearError,
    displayId,
    accountId,
    activationId,
  };
};
